import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { HardwireLogo } from "../components/HardwireMark";
import { LayoutDashboard, FolderKanban, FileText, CalendarClock, CreditCard, ShieldCheck, LogOut, Bell } from "lucide-react";
import "../app-light.css";

const NAV = [
  { to: "", label: "Dashboard", icon: LayoutDashboard },
  { to: "projects", label: "My Projects", icon: FolderKanban },
  { to: "estimates", label: "Estimates", icon: FileText },
  { to: "appointments", label: "Appointments", icon: CalendarClock },
  { to: "payments", label: "Payments", icon: CreditCard },
  { to: "requests", label: "Requests & Messages", icon: FileText },
  { to: "warranty", label: "Warranty", icon: ShieldCheck },
];

export function CustomerShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();

  const Sidebar = (
    <>
      <div className="px-1 py-2">
        <HardwireLogo size={38} tone="light" />
      </div>
      <nav className="mt-8 flex flex-1 flex-col gap-1" data-testid="customer-nav">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={`/portal/${n.to}`}
            end={n.to === ""}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold transition ${
                isActive ? "bg-[#1286F2] text-white shadow-lg shadow-[#1286F2]/20" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
            data-testid={`cnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <button
        onClick={async () => { await logout(); navigate("/login"); }}
        className="mt-4 flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold text-slate-400 transition hover:bg-white/5 hover:text-rose-300"
        data-testid="customer-logout"
      >
        <LogOut size={18} /> Log Out
      </button>
    </>
  );

  return (
    <div className="app-light flex min-h-screen bg-slate-50 text-slate-900">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-72 shrink-0 flex-col bg-[#0B1120] p-5 lg:flex">
        {Sidebar}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-5 py-3 backdrop-blur">
          {/* Mobile nav */}
          <nav className="flex min-w-0 flex-1 gap-1 overflow-x-auto lg:hidden" data-testid="customer-nav-mobile">
            {NAV.map((n) => (
              <NavLink key={n.to} to={`/portal/${n.to}`} end={n.to === ""}
                className={({ isActive }) => `flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold ${isActive ? "bg-[#1286F2] text-white" : "text-slate-500"}`} data-testid={`customer-mobile-${n.to || 'home'}`}>
                <n.icon size={14} /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <button onClick={() => navigate('/portal/requests')} className="relative rounded-lg p-2 text-slate-500 hover:text-slate-800" data-testid="notif-bell" aria-label="Open messages and requests">
              <Bell size={20} />
            </button>
            <button onClick={async () => { await logout(); navigate('/login'); }} className="rounded-lg p-2 text-slate-500 lg:hidden" aria-label="Log out" data-testid="mobile-customer-logout"><LogOut size={18} /></button>
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1286F2] text-sm font-bold text-white">{initial}</div>
              <div className="hidden text-sm font-semibold text-slate-800 sm:block" data-testid="customer-name">{user?.name}</div>
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
